"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { HiFilm, HiQuestionMarkCircle, HiUpload, HiDownload, HiRefresh } from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";

const CANVAS_W = 1280;
const CANVAS_H = 720;

type FontSize = "large" | "medium" | "small";
type TextColor = "white" | "yellow" | "red";
type TextPosition = "top" | "center" | "bottom";

const FONT_SIZE_MAP: Record<FontSize, number> = {
  large: 90,
  medium: 60,
  small: 40,
};

const TEXT_COLOR_MAP: Record<TextColor, string> = {
  white: "#FFFFFF",
  yellow: "#FFE000",
  red: "#FF3B3B",
};

const faqItems = [
  {
    question: "What size are the thumbnails?",
    answer:
      "Thumbnails are generated at 1280x720 pixels — the standard YouTube recommended thumbnail size (16:9 ratio). This matches YouTube's preferred dimensions and works across all devices and display sizes.",
  },
  {
    question: "What file format is the download?",
    answer:
      "Thumbnails are downloaded as PNG files using the browser's Canvas API. PNG is a lossless format that produces sharp text and clear edges, making it ideal for thumbnails that will be compressed by YouTube or other platforms.",
  },
  {
    question: "Does this tool store my images?",
    answer:
      "No. Everything is processed entirely in your browser. Your uploaded image is loaded into an HTML Canvas element, the text is drawn on top, and you download the result directly. No image data is ever sent to a server.",
  },
  {
    question: "Why add a dark overlay?",
    answer:
      "A semi-transparent dark overlay behind the text dramatically improves text readability against any background image. Without it, white or colored text can be hard to read on bright or complex backgrounds. The overlay opacity slider lets you control how strong the effect is.",
  },
  {
    question: "What background images work best?",
    answer:
      "High-contrast images with a clear subject and some empty space for text work best. Action shots, close-up portraits, and bold graphics all perform well. Avoid images that are too busy or have complex patterns in the area where your text will appear.",
  },
];

export default function VideoThumbnailGeneratorClient() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [bgImage, setBgImage] = useState<HTMLImageElement | null>(null);
  const [bgPreviewUrl, setBgPreviewUrl] = useState<string | null>(null);
  const [text, setText] = useState("Your Title Here");
  const [fontSize, setFontSize] = useState<FontSize>("large");
  const [textColor, setTextColor] = useState<TextColor>("white");
  const [textPosition, setTextPosition] = useState<TextPosition>("center");
  const [overlayOpacity, setOverlayOpacity] = useState(40); // 0-100
  const [isDragging, setIsDragging] = useState(false);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Background
    if (bgImage) {
      // Cover-fit: maintain aspect ratio, fill canvas
      const imgAspect = bgImage.width / bgImage.height;
      const canvasAspect = CANVAS_W / CANVAS_H;
      let sx = 0,
        sy = 0,
        sw = bgImage.width,
        sh = bgImage.height;
      if (imgAspect > canvasAspect) {
        sw = bgImage.height * canvasAspect;
        sx = (bgImage.width - sw) / 2;
      } else {
        sh = bgImage.width / canvasAspect;
        sy = (bgImage.height - sh) / 2;
      }
      ctx.drawImage(bgImage, sx, sy, sw, sh, 0, 0, CANVAS_W, CANVAS_H);
    } else {
      // Default gradient background
      const gradient = ctx.createLinearGradient(0, 0, CANVAS_W, CANVAS_H);
      gradient.addColorStop(0, "#1a1a2e");
      gradient.addColorStop(1, "#16213e");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    }

    // Dark overlay
    if (overlayOpacity > 0) {
      ctx.fillStyle = `rgba(0, 0, 0, ${overlayOpacity / 100})`;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    }

    // Text
    if (text.trim()) {
      const px = FONT_SIZE_MAP[fontSize];
      ctx.font = `bold ${px}px Arial, sans-serif`;
      ctx.fillStyle = TEXT_COLOR_MAP[textColor];
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Text shadow for readability
      ctx.shadowColor = "rgba(0,0,0,0.8)";
      ctx.shadowBlur = 20;
      ctx.shadowOffsetX = 3;
      ctx.shadowOffsetY = 3;

      // Word-wrap
      const maxWidth = CANVAS_W - 120;
      const words = text.trim().split(" ");
      const lines: string[] = [];
      let currentLine = "";

      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(testLine).width > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) lines.push(currentLine);

      const lineHeight = px * 1.25;
      const totalHeight = lines.length * lineHeight;

      let startY: number;
      if (textPosition === "top") {
        startY = 100 + px / 2;
      } else if (textPosition === "bottom") {
        startY = CANVAS_H - 100 - totalHeight + px / 2;
      } else {
        startY = CANVAS_H / 2 - totalHeight / 2 + px / 2;
      }

      lines.forEach((line, i) => {
        ctx.fillText(line, CANVAS_W / 2, startY + i * lineHeight, maxWidth);
      });

      // Reset shadow
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    }
  }, [bgImage, text, fontSize, textColor, textPosition, overlayOpacity]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const handleImageFile = (file: File) => {
    if (!file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    setBgPreviewUrl(url);
    const img = new Image();
    img.onload = () => {
      setBgImage(img);
    };
    img.src = url;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImageFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleImageFile(file);
  };

  const downloadPng = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "thumbnail.png";
    link.href = canvas.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const reset = () => {
    setBgImage(null);
    setBgPreviewUrl(null);
    setText("Your Title Here");
    setFontSize("large");
    setTextColor("white");
    setTextPosition("center");
    setOverlayOpacity(40);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Scale factor for the display preview (canvas is 1280x720, display at half)
  const PREVIEW_SCALE = 0.5;
  const previewW = CANVAS_W * PREVIEW_SCALE;
  const previewH = CANVAS_H * PREVIEW_SCALE;

  return (
    <div className="container px-4 py-12 mx-auto max-w-5xl">
      <ToolPageSchema
        name="Video Thumbnail Generator"
        description="Create 1280x720 video thumbnails in your browser. Upload a background image, add text overlays, and download as PNG."
        url="https://plugmonkey.xyz/tool/video-thumbnail-generator/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Video Thumbnail Generator",
            url: "https://plugmonkey.xyz/tool/video-thumbnail-generator/",
          },
        ]}
      />

      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-primary/10 rounded-full text-primary">
          <HiFilm className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Video Thumbnail Generator</h1>
          <p className="text-muted-foreground mt-1">
            Create 1280x720 thumbnails with text overlays. Upload a background, customize, and
            download as PNG.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          A compelling thumbnail is often the difference between a click and a scroll-past. This
          tool lets you create YouTube-ready 1280x720 thumbnails directly in your browser — no
          Photoshop, no Canva subscription needed.
        </p>
        <p>
          Upload your background image, type your title, choose your text style, and download the
          result as a PNG. Everything runs locally in your browser — your images are never uploaded
          to a server.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Controls */}
        <div className="lg:col-span-2 space-y-5">
          {/* Background Image */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Background Image
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!bgPreviewUrl ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
                    isDragging
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/60 hover:bg-primary/5"
                  }`}
                >
                  <HiUpload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Drop image or click to browse</p>
                  <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <img
                    src={bgPreviewUrl}
                    alt="Background preview"
                    className="w-full rounded-lg object-cover h-24"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Change Image
                  </Button>
                </div>
              )}
              <input
                id="vtg-background"
                name="background-image"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </CardContent>
          </Card>

          {/* Text */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Title Text
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="vtg-text">Text</Label>
                <Input
                  id="vtg-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Your thumbnail title..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vtg-fontsize">Font Size</Label>
                <select
                  id="vtg-fontsize"
                  value={fontSize}
                  onChange={(e) => setFontSize(e.target.value as FontSize)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="large">Large (90px)</option>
                  <option value="medium">Medium (60px)</option>
                  <option value="small">Small (40px)</option>
                </select>
              </div>

              <div className="space-y-2">
                {/* Group heading, not a <label>: these are buttons, so the
                    heading names the group via aria-labelledby and each button
                    reports its selected state with aria-pressed. */}
                <span id="vtg-text-color-label" className="text-sm font-medium leading-none">
                  Text Color
                </span>
                <div className="flex gap-2" role="group" aria-labelledby="vtg-text-color-label">
                  {(["white", "yellow", "red"] as TextColor[]).map((c) => (
                    <button
                      key={c}
                      onClick={() => setTextColor(c)}
                      aria-pressed={textColor === c}
                      className={`flex-1 py-2 rounded-lg border-2 capitalize text-sm font-medium transition-all ${
                        textColor === c
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/40"
                      }`}
                    >
                      <span
                        className="inline-block w-3 h-3 rounded-full mr-1 align-middle border border-border"
                        style={{ background: TEXT_COLOR_MAP[c] }}
                      />
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <span id="vtg-text-position-label" className="text-sm font-medium leading-none">
                  Text Position
                </span>
                <div className="flex gap-2" role="group" aria-labelledby="vtg-text-position-label">
                  {(["top", "center", "bottom"] as TextPosition[]).map((p) => (
                    <button
                      key={p}
                      onClick={() => setTextPosition(p)}
                      aria-pressed={textPosition === p}
                      className={`flex-1 py-2 rounded-lg border-2 capitalize text-sm font-medium transition-all ${
                        textPosition === p
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/40"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Overlay */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Dark Overlay
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <Label htmlFor="vtg-overlay">Opacity</Label>
                  <span className="text-muted-foreground">{overlayOpacity}%</span>
                </div>
                <input
                  id="vtg-overlay"
                  type="range"
                  min={0}
                  max={80}
                  value={overlayOpacity}
                  onChange={(e) => setOverlayOpacity(Number(e.target.value))}
                  className="w-full accent-primary"
                />
                <p className="text-xs text-muted-foreground">
                  Increases text readability over busy backgrounds.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button onClick={downloadPng} className="flex-1">
              <HiDownload className="w-4 h-4 mr-2" />
              Download PNG
            </Button>
            <Button variant="outline" onClick={reset}>
              <HiRefresh className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div className="lg:col-span-3">
          <div className="sticky top-6">
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Preview (1280x720)
            </p>
            <div
              className="rounded-xl overflow-hidden border border-border shadow-lg bg-muted"
              style={{ width: previewW, height: previewH, maxWidth: "100%" }}
            >
              <canvas
                ref={canvasRef}
                width={CANVAS_W}
                height={CANVAS_H}
                style={{
                  width: previewW,
                  height: previewH,
                  maxWidth: "100%",
                  display: "block",
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Preview is scaled 50%. Downloaded PNG is full 1280x720.
            </p>
            <Button onClick={downloadPng} className="w-full mt-3">
              <HiDownload className="w-4 h-4 mr-2" />
              Download 1280x720 PNG
            </Button>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ items={faqItems} showSupportLink={false} className="space-y-4" />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
